import {
  DATASTAR_SIGNAL_EVENT,
  type OnRemovalFn,
  type DatastarSignalEvent,
  type Dependency,
} from './types'

// const from = 'namespacedSignals'

const added = new Set<string>()
const removed = new Set<string>()
const updated = new Set<string>()

export function batch<T>(fn: () => T): T {
  added.clear()
  removed.clear()
  updated.clear()

  const result = fn()

  if (added.size || removed.size || updated.size) {
    document.dispatchEvent(
      new CustomEvent<DatastarSignalEvent>(DATASTAR_SIGNAL_EVENT, {
        detail: {
          added: [...added],
          removed: [...removed],
          updated: [...updated],
        },
      }),
    )
  }

  return result
}

// sorted by path
const dependencyPaths: string[] = []
const dependencies = new Map<string, Dependency>()

export function depPaths(): readonly string[] {
  return dependencyPaths
}

export function dep(path: string): Dependency | undefined {
  return dependencies.get(path)
}

function depRange(prefix: string) {
  const results: string[] = []
  // binary search for the range of paths that start with prefix
  let left = 0
  let right = dependencyPaths.length - 1
  const predicate = (s: string) => s !== prefix && !s.startsWith(`${prefix}.`)
  while (left <= right) {
    const mid = Math.floor((left + right) / 2)
    const midPath = dependencyPaths[mid]
    if (midPath < prefix) {
      left = mid + 1
    } else if (midPath > prefix) {
      right = mid - 1
    } else {
      // found the prefix, now find the range
      let start = mid

      do {
        const s = dependencyPaths[start]
        if (predicate(s)) break
        start--
      } while (start >= 0)

      let end = mid
      do {
        const s = dependencyPaths[end]
        if (predicate(s)) break
        end++
      } while (end < dependencyPaths.length)
      for (let i = start + 1; i < end; i++) {
        results.push(dependencyPaths[i])
      }
      return results
    }
  }

  return results
}

export function setDep(path: string, dep: Dependency) {
  removeDeps(path)
  dependencyPaths.push(path)
  dependencies.set(path, dep)
  dependencyPaths.sort() // keep paths sorted
  added.add(path)
}

export function setDepValue<T>(path: string, value: T) {
  const existingSignal = dependencies.get(path)
  if (existingSignal) {
    if (!(existingSignal instanceof Signal)) {
      return
    }

    existingSignal.value = value
  } else {
    signal(value, path)
  }
}

export function depValue<T>(path: string) {
  return dependencies.get(path)?.value as T | undefined
}

export function upsertIfMissing<T>(path: string, defaultValue: T) {
  let inserted = false
  let dep = dependencies.get(path)
  if (!dep) {
    inserted = true
    dep = signal(defaultValue, path)
  }
  return { dep, inserted }
}

export function mergeDeps(toMerge: NestedValues, onlyIfMissing = false) {
  const flattened = flatten(toMerge)
  for (const [path, value] of Object.entries(flattened)) {
    const hasKey = dependencies.has(path)
    if (hasKey && onlyIfMissing) continue
    setDepValue(path, value)
  }
}

export function removeDeps(...paths: string[]) {
  // remove the path from dependencies with the given path prefix
  for (const path of paths) {
    const subPaths = depRange(path)
    for (const subPath of subPaths) {
      dependencies.delete(subPath)
      const index = dependencyPaths.indexOf(subPath)
      if (index > -1) {
        dependencyPaths.splice(index, 1)
      }
      removed?.add(subPath)
    }
  }
}

export function json(shouldIndent = true, onlyPublic = false) {
  return JSON.stringify(nested(onlyPublic), null, shouldIndent ? 2 : 0)
}

export type NestedValues = { [key: string]: NestedValues | any }

export function nested(onlyPublic: boolean, ...subPaths: string[]) {
  const filtered = new Map<string, any>()
  for (const path of dependencyPaths) {
    if (onlyPublic && path.match(/^_|\._/)) {
      continue
    }
    if (subPaths.length > 0) {
      let found = false
      for (const subPath of subPaths) {
        if (path.startsWith(subPath)) {
          found = true
          break
        }
      }
      if (!found) {
        continue
      }
    }
    filtered.set(path, dependencies.get(path)?.value)
  }
  const nv = unflatten(filtered)
  return nv
}

function isPlainObject(value: any): boolean {
  return Object.prototype.toString.call(value) === '[object Object]'
}

function flatten(
  obj: Record<string, any>,
  roots: string[] = [],
  sep = '.',
): object {
  return Object.keys(obj).reduce((memo: Record<string, any>, prop: string) => {
    return Object.assign(
      {},
      memo,
      isPlainObject(obj[prop])
        ? flatten(obj[prop], roots.concat([prop]), sep)
        : { [roots.concat([prop]).join(sep)]: obj[prop] },
    )
  }, {})
}

function unflatten(obj: Map<string, Dependency>, sep = '.'): NestedValues {
  const result: NestedValues = {}
  for (const [key, value] of obj.entries()) {
    const keys = key.split(sep)
    let current = result
    for (let i = 0; i < keys.length - 1; i++) {
      const k = keys[i]
      if (!current[k]) {
        current[k] = {}
      }
      current = current[k]
    }
    current[keys[keys.length - 1]] = value
  }
  return result
}

export function evalRX<T>(
  rxFn: (...argsThenDeps: any[]) => T,
  dm: boolean[],
  deps: Dependency[],
  ...args: any[]
) {
  return batch(() =>
    rxFn(...args, ...deps.map((dep, index) => (dm[index] ? dep : dep.value))),
  )
}

interface AlienDependency {
  subs?: Link
  subsTail?: Link
}

interface Subscriber {
  flags: SubscriberFlags
  deps?: Link
  depsTail?: Link
}

interface Link {
  dep: AlienDependency | (AlienDependency & Subscriber)
  sub: Subscriber | (AlienDependency & Subscriber)
  prevSub?: Link
  nextSub?: Link
  nextDep?: Link
}

interface OneWayLink<T> {
  target: T
  linked?: OneWayLink<T>
}

// biome-ignore lint/suspicious/noConstEnum: <explanation>
const enum SubscriberFlags {
  Computed = 1 << 0,
  Effect = 1 << 1,
  Tracking = 1 << 2,
  Notified = 1 << 3,
  Recursed = 1 << 4,
  Dirty = 1 << 5,
  PendingComputed = 1 << 6,
  PendingEffect = 1 << 7,
  Propagated = Dirty | PendingComputed | PendingEffect,
}

const args: any[][] = []
const notifyBuffer: (Subscriber | undefined)[] = []
let notifyIndex = 0
let notifyBufferLength = 0

function getter<T>(deps: Dependency[], fn: (...args: any[]) => T): () => T {
  switch (deps.length) {
    case 0:
      return fn
    case 1:
      return () => fn(deps[0].value)
    case 2:
      return () => fn(deps[0].value, deps[1].value)
    case 3:
      return () => fn(deps[0].value, deps[1].value, deps[2].value)
    case 4:
      return () =>
        fn(deps[0].value, deps[1].value, deps[2].value, deps[3].value)
  }

  const len = deps.length
  if (!args[len]) {
    args[len] = Array(len)
  }

  return () => {
    for (let i = 0; i < len; i++) {
      args[len][i] = deps[i].value
    }
    return fn(...args[len])
  }
}

function updateComputed(computed: Computed) {
  computed.flags &= ~(
    SubscriberFlags.Notified |
    SubscriberFlags.Recursed |
    SubscriberFlags.Propagated
  )
  const oldValue = computed.currentValue
  const newValue = computed.getter()
  if (oldValue !== newValue) {
    computed.currentValue = newValue
    // if (computed.path) {
    //   updated.add(computed.path)
    // }
    return true
  }
  return false
}

function notifyEffect(effect: Effect) {
  const flags = effect.flags
  if (
    flags & SubscriberFlags.Dirty ||
    (flags & SubscriberFlags.PendingComputed && updateDirtyFlag(effect, flags))
  ) {
    effect.flags &= ~(
      SubscriberFlags.Notified |
      SubscriberFlags.Recursed |
      SubscriberFlags.Propagated
    )
    effect.run()
  }
  return true
}

/**
 * Updates the dirty flag for the given subscriber based on its dependencies.
 *
 * If the subscriber has any pending computeds, this function sets the Dirty flag
 * and returns `true`. Otherwise, it clears the PendingComputed flag and returns `false`.
 *
 * @param sub - The subscriber to update.
 * @param flags - The current flag set for this subscriber.
 * @returns `true` if the subscriber is marked as Dirty; otherwise `false`.
 */
function updateDirtyFlag(sub: Subscriber, flags: SubscriberFlags): boolean {
  if (checkDirty(sub.deps!)) {
    sub.flags = flags | SubscriberFlags.Dirty
    return true
  }
  sub.flags = flags & ~SubscriberFlags.PendingComputed
  return false
}

export function signal<T>(value: T, path?: string): Signal<T> {
  return new Signal(value, path)
}

export class Signal<T = unknown> implements AlienDependency {
  // Dependency fields
  subs?: Link
  subsTail?: Link

  constructor(
    public currentValue: T,
    public path?: string,
  ) {
    if (path) {
      setDep(path, this)
    }
  }

  get value(): T {
    return this.currentValue
  }

  set value(value: T) {
    if (this.currentValue !== value) {
      this.currentValue = value

      if (this.path) {
        updated.add(this.path)
      }
      if (this.subs) {
        // propagate
        let current = this.subs
        let next = current.nextSub
        let branchs: OneWayLink<Link | undefined> | undefined
        let branchDepth = 0
        let targetFlag = SubscriberFlags.Dirty

        top: do {
          const sub = current.sub
          const subFlags = sub.flags

          let shouldNotify = false

          if (
            !(
              subFlags &
              (SubscriberFlags.Tracking |
                SubscriberFlags.Recursed |
                SubscriberFlags.Propagated)
            )
          ) {
            sub.flags = subFlags | targetFlag | SubscriberFlags.Notified
            shouldNotify = true
          } else if (
            subFlags & SubscriberFlags.Recursed &&
            !(subFlags & SubscriberFlags.Tracking)
          ) {
            sub.flags =
              (subFlags & ~SubscriberFlags.Recursed) |
              targetFlag |
              SubscriberFlags.Notified
            shouldNotify = true
          } else if (
            !(subFlags & SubscriberFlags.Propagated) &&
            isValidLink(current, sub)
          ) {
            sub.flags =
              subFlags |
              SubscriberFlags.Recursed |
              targetFlag |
              SubscriberFlags.Notified
            shouldNotify = !!(sub as AlienDependency).subs
          }

          if (shouldNotify) {
            const subSubs = (sub as AlienDependency).subs
            if (subSubs) {
              current = subSubs
              if (subSubs.nextSub) {
                branchs = { target: next, linked: branchs }
                ++branchDepth
                next = current.nextSub
                targetFlag = SubscriberFlags.PendingComputed
              } else {
                targetFlag =
                  subFlags & SubscriberFlags.Effect
                    ? SubscriberFlags.PendingEffect
                    : SubscriberFlags.PendingComputed
              }
              continue
            }
            if (subFlags & SubscriberFlags.Effect) {
              notifyBuffer[notifyBufferLength++] = sub
            }
          } else if (!(subFlags & (SubscriberFlags.Tracking | targetFlag))) {
            sub.flags = subFlags | targetFlag | SubscriberFlags.Notified
            if (
              (subFlags &
                (SubscriberFlags.Effect | SubscriberFlags.Notified)) ===
              SubscriberFlags.Effect
            ) {
              notifyBuffer[notifyBufferLength++] = sub
            }
          } else if (
            !(subFlags & targetFlag) &&
            subFlags & SubscriberFlags.Propagated &&
            isValidLink(current, sub)
          ) {
            sub.flags = subFlags | targetFlag
          }

          if ((current = next!)) {
            next = current.nextSub
            targetFlag = branchDepth
              ? SubscriberFlags.PendingComputed
              : SubscriberFlags.Dirty
            continue
          }

          while (branchDepth--) {
            current = branchs!.target!
            branchs = branchs!.linked
            if (current) {
              next = current.nextSub
              targetFlag = branchDepth
                ? SubscriberFlags.PendingComputed
                : SubscriberFlags.Dirty
              continue top
            }
          }

          break
          // biome-ignore lint/correctness/noConstantCondition: <explanation>
        } while (true)

        // process effect notifications
        while (notifyIndex < notifyBufferLength) {
          const effect = notifyBuffer[notifyIndex]!
          notifyBuffer[notifyIndex++] = undefined
          if (!notifyEffect(effect as Effect)) {
            effect.flags &= ~SubscriberFlags.Notified
          }
        }
        notifyIndex = 0
        notifyBufferLength = 0
      }
    }
  }
}

export function computed<T>(
  deps: Dependency[],
  fn: (...args: any[]) => T,
  path?: string,
): Computed<T> {
  return new Computed(deps, getter(deps, fn), path)
}

export class Computed<T = unknown> implements Subscriber, AlienDependency {
  currentValue?: T
  getter: () => T

  // Dependency fields
  subs?: Link
  subsTail?: Link

  // Subscriber fields
  deps?: Link
  depsTail?: Link
  flags: SubscriberFlags = SubscriberFlags.Computed | SubscriberFlags.Dirty

  constructor(
    deps: Dependency[],
    fn: (...args: any[]) => T,
    public path?: string,
  ) {
    this.getter = getter(deps, fn)
    if (path) {
      setDep(path, this)
    }

    for (const dep of deps) {
      link(dep, this)
    }
  }

  get value(): T {
    const flags = this.flags
    if (flags & (SubscriberFlags.PendingComputed | SubscriberFlags.Dirty)) {
      if (flags & SubscriberFlags.Dirty || checkDirty(this.deps!)) {
        if (updateComputed(this)) {
          const subs = this.subs
          if (subs) {
            shallowPropagate(subs)
          }
        }
      } else {
        this.flags = flags & ~SubscriberFlags.PendingComputed
      }
    }
    return this.currentValue!
  }
}

export function effect(
  deps: Dependency[],
  fn: (...args: any[]) => void,
): OnRemovalFn {
  const e = new Effect(deps, fn)
  return () => e.dispose()
}

export class Effect implements Subscriber {
  deps?: Link
  depsTail?: Link
  flags: SubscriberFlags = SubscriberFlags.Effect
  run: () => void

  constructor(deps: Dependency[], fn: (...args: any[]) => void) {
    for (const dep of deps) {
      link(dep, this)
    }

    this.run = getter(deps, fn)
    this.run()
  }

  dispose() {
    this.depsTail = undefined
    this.flags &= ~(
      SubscriberFlags.Notified |
      SubscriberFlags.Recursed |
      SubscriberFlags.Propagated
    )

    if (this.deps) {
      let link = this.deps
      do {
        const dep = link.dep
        const nextDep = link.nextDep
        const nextSub = link.nextSub
        const prevSub = link.prevSub

        if (nextSub) {
          nextSub.prevSub = prevSub
        } else {
          dep.subsTail = prevSub
        }

        if (prevSub) {
          prevSub.nextSub = nextSub
        } else {
          dep.subs = nextSub
        }

        if (!dep.subs && 'deps' in dep) {
          const depFlags = dep.flags
          if (!(depFlags & SubscriberFlags.Dirty)) {
            dep.flags = depFlags | SubscriberFlags.Dirty
          }
          const depDeps = dep.deps
          if (depDeps) {
            link = depDeps
            dep.depsTail!.nextDep = nextDep
            dep.deps = undefined
            dep.depsTail = undefined
            continue
          }
        }
        link = nextDep!
      } while (link)

      this.deps = undefined
    }
  }
}

/**
 * Links a given dependency and subscriber if they are not already linked.
 *
 * @param dep - The dependency to be linked.
 * @param sub - The subscriber that depends on this dependency.
 * @returns The newly created link object if the two are not already linked; otherwise `undefined`.
 */
function link(dep: AlienDependency, sub: Subscriber): Link | undefined {
  const currentDep = sub.depsTail
  if (currentDep && currentDep.dep === dep) {
    return
  }
  const nextDep = currentDep ? currentDep.nextDep : sub.deps
  if (nextDep && nextDep.dep === dep) {
    sub.depsTail = nextDep
    return
  }
  const depLastSub = dep.subsTail
  if (depLastSub && depLastSub.sub === sub && isValidLink(depLastSub, sub)) {
    return
  }

  const newLink: Link = {
    dep,
    sub,
    nextDep,
    prevSub: undefined,
    nextSub: undefined,
  }
  if (!currentDep) {
    sub.deps = newLink
  } else {
    currentDep.nextDep = newLink
  }
  if (!dep.subs) {
    dep.subs = newLink
  } else {
    const oldTail = dep.subsTail!
    newLink.prevSub = oldTail
    oldTail.nextSub = newLink
  }
  sub.depsTail = newLink
  dep.subsTail = newLink
  return newLink
}

/**
 * Recursively checks and updates all computed subscribers marked as pending.
 *
 * It traverses the linked structure using a stack mechanism. For each computed
 * subscriber in a pending state, updateComputed is called and shallowPropagate
 * is triggered if a value changes. Returns whether any updates occurred.
 *
 * @param current - The starting link representing a sequence of pending computeds.
 * @returns `true` if a computed was updated, otherwise `false`.
 */
function checkDirty(current: Link): boolean {
  let prevLinks: OneWayLink<Link> | undefined
  let checkDepth = 0
  let dirty: boolean

  top: do {
    dirty = false
    const dep = current.dep

    if (current.sub.flags & SubscriberFlags.Dirty) {
      dirty = true
    } else if ('flags' in dep) {
      const depFlags = dep.flags
      if (
        (depFlags & (SubscriberFlags.Computed | SubscriberFlags.Dirty)) ===
        (SubscriberFlags.Computed | SubscriberFlags.Dirty)
      ) {
        if (updateComputed(dep as Computed)) {
          const subs = dep.subs!
          if (subs.nextSub) {
            shallowPropagate(subs)
          }
          dirty = true
        }
      } else if (
        (depFlags &
          (SubscriberFlags.Computed | SubscriberFlags.PendingComputed)) ===
        (SubscriberFlags.Computed | SubscriberFlags.PendingComputed)
      ) {
        if (current.nextSub || current.prevSub) {
          prevLinks = { target: current, linked: prevLinks }
        }
        current = dep.deps!
        ++checkDepth
        continue
      }
    }

    if (!dirty && current.nextDep) {
      current = current.nextDep
      continue
    }

    while (checkDepth) {
      --checkDepth
      const sub = current.sub as AlienDependency & Subscriber
      const firstSub = sub.subs!
      if (dirty) {
        if (updateComputed(sub as Computed)) {
          if (firstSub.nextSub) {
            current = prevLinks!.target
            prevLinks = prevLinks!.linked
            shallowPropagate(firstSub)
          } else {
            current = firstSub
          }
          continue
        }
      } else {
        sub.flags &= ~SubscriberFlags.PendingComputed
      }
      if (firstSub.nextSub) {
        current = prevLinks!.target
        prevLinks = prevLinks!.linked
      } else {
        current = firstSub
      }
      if (current.nextDep) {
        current = current.nextDep
        continue top
      }
      dirty = false
    }

    return dirty
    // biome-ignore lint/correctness/noConstantCondition: <explanation>
  } while (true)
}

/**
 * Quickly propagates PendingComputed status to Dirty for each subscriber in the chain.
 *
 * If the subscriber is also marked as an effect, it is added to the queuedEffects list
 * for later processing.
 *
 * @param link - The head of the linked list to process.
 */
function shallowPropagate(link: Link): void {
  do {
    const sub = link.sub
    const subFlags = sub.flags
    if (
      (subFlags & (SubscriberFlags.PendingComputed | SubscriberFlags.Dirty)) ===
      SubscriberFlags.PendingComputed
    ) {
      sub.flags = subFlags | SubscriberFlags.Dirty | SubscriberFlags.Notified
      if (
        (subFlags & (SubscriberFlags.Effect | SubscriberFlags.Notified)) ===
        SubscriberFlags.Effect
      ) {
        notifyBuffer[notifyBufferLength++] = sub
      }
    }
    link = link.nextSub!
  } while (link)
}

/**
 * Verifies whether the given link is valid for the specified subscriber.
 *
 * It iterates through the subscriber's link list (from sub.deps to sub.depsTail)
 * to determine if the provided link object is part of that chain.
 *
 * @param checkLink - The link object to validate.
 * @param sub - The subscriber whose link list is being checked.
 * @returns `true` if the link is found in the subscriber's list; otherwise `false`.
 */
function isValidLink(checkLink: Link, sub: Subscriber): boolean {
  const depsTail = sub.depsTail
  if (depsTail) {
    let link = sub.deps!
    do {
      if (link === checkLink) {
        return true
      }
      if (link === depsTail) {
        break
      }
      link = link.nextDep!
    } while (link)
  }
  return false
}
